import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  Chip,
  Fade,
  useMediaQuery,
  Alert,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import { useTheme, alpha, styled } from '@mui/material/styles';
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useQuery, useMutation } from "react-query";
import client from "../../../../../API";
import ChatScreen from "../../../../../Components/ChatScreen";

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const Z_INDEX = {
  CHAT_DRAWER: 1300,
};

// ═══════════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const FullscreenDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: Z_INDEX.CHAT_DRAWER,
  '& .MuiDrawer-paper': {
    height: '100vh',
    width: '100vw',
    maxHeight: '100vh',
    maxWidth: '100vw',
    borderRadius: 0,
    background: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  backdropFilter: 'blur(10px)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  flexShrink: 0,
  minHeight: 64,
}));

const ChipsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  background: alpha(theme.palette.background.paper, 0.8),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  backdropFilter: 'blur(8px)',
  flexShrink: 0,
}));

const ChatContainer = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
});

const ErrorContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  gap: theme.spacing(3),
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  gap: theme.spacing(2),
}));

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

const getApplicantDisplayName = (applicant, castingCall) => {
  if (!applicant || !castingCall) return 'Loading...';
  
  if (castingCall.call_type === 'creator') {
    return applicant.display_name || applicant.username || 'Creator';
  }
  
  const fullName = `${applicant.first_name || ''} ${applicant.last_name || ''}`.trim();
  return fullName || applicant.username || applicant.email || 'User';
};

const getApplicantAvatar = (applicant) => {
  if (!applicant) return null;
  return applicant.pfphref || applicant.phphref || null;
};

const getApplicantId = (applicant, castingCall) => {
  if (!applicant || !castingCall) return null;
  return castingCall.call_type === 'creator' ? applicant.username : applicant.id;
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function CastingCallChatDrawer({
  open,
  onClose,
  castingCall,
  applicant,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const chatRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────

  const [error, setError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // MEMOIZED VALUES
  // ─────────────────────────────────────────────────────────────────────────────

  const castingCallId = castingCall?.id;
  const applicantId = useMemo(
    () => getApplicantId(applicant, castingCall),
    [applicant, castingCall]
  );
  const displayName = useMemo(
    () => getApplicantDisplayName(applicant, castingCall),
    [applicant, castingCall]
  );
  const avatarUrl = useMemo(
    () => getApplicantAvatar(applicant),
    [applicant]
  );

  const isReady = Boolean(
    open && 
    castingCallId && 
    applicantId && 
    applicant && 
    castingCall
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // QUERY: Check for existing conversation
  // ─────────────────────────────────────────────────────────────────────────────

  const {
    data: checkData,
    refetch: refetchCheck,
    isLoading: isCheckingConversation,
    isError: hasCheckError,
    error: checkError,
  } = useQuery(
    ["checkCastingCallConversation", castingCallId, applicantId],
    () => client.castingCalls.checkConversation(castingCallId, applicantId),
    {
      enabled: isReady,
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30000, // 30 seconds
      onError: (err) => {
        console.error('❌ Error checking conversation:', err);
        const errorMessage = err?.response?.data?.error || err.message || 'Failed to check conversation';
        setError(errorMessage);
      },
      onSuccess: (data) => {
        console.log('✅ Conversation check result:', data);
        setError(null);
      },
    }
  );

  const conversationId = checkData?.conversation_id;
  const conversationExists = checkData?.exists || Boolean(conversationId);

  // ─────────────────────────────────────────────────────────────────────────────
  // MUTATION: Create conversation
  // ─────────────────────────────────────────────────────────────────────────────

  const createConversationMutation = useMutation(
    () => {
      console.log('🔨 Creating conversation for applicant:', applicantId);
      const payload = { applicant_id: applicantId };
      return client.castingCalls.createConversation(castingCallId, payload);
    },
    {
      onMutate: () => {
        console.log('⏳ Starting conversation creation...');
        setIsCreating(true);
        setError(null);
      },
      onSuccess: (data) => {
        console.log('✅ Conversation created successfully:', data);
        setError(null);
        setIsCreating(false);
        
        // Refetch to get the new conversation ID
        setTimeout(() => {
          refetchCheck();
        }, 500);
      },
      onError: (err) => {
        console.error('❌ Error creating conversation:', err);
        const errorMessage = err?.response?.data?.error || err.message || 'Failed to create conversation';
        setError(errorMessage);
        setIsCreating(false);
      },
    }
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Auto-create conversation if it doesn't exist
  useEffect(() => {
    if (
      isReady &&
      checkData &&
      !conversationExists &&
      !isCreating &&
      !createConversationMutation.isLoading &&
      !error
    ) {
      console.log('🚀 Auto-creating conversation...');
      createConversationMutation.mutate();
    }
  }, [
    isReady,
    checkData,
    conversationExists,
    isCreating,
    createConversationMutation,
    error,
  ]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const scrollY = window.scrollY;
      
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.left = '0';
      document.body.style.right = '0';
      
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.left = '';
        document.body.style.right = '';
        if (scrollY) {
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [open]);

  // Reset error when drawer closes
  useEffect(() => {
    if (!open) {
      setError(null);
      setIsCreating(false);
    }
  }, [open]);

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  const handleRetry = useCallback(() => {
    console.log('🔄 Retrying conversation check/creation...');
    setError(null);
    
    if (conversationExists) {
      // Just refetch the check
      refetchCheck();
    } else {
      // Try creating again
      createConversationMutation.mutate();
    }
  }, [conversationExists, refetchCheck, createConversationMutation]);

  const handleClose = useCallback(() => {
    setError(null);
    setIsCreating(false);
    onClose();
  }, [onClose]);

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER HELPERS
  // ─────────────────────────────────────────────────────────────────────────────

  const renderError = () => (
    <ErrorContainer>
      <ErrorOutlineIcon 
        sx={{ 
          fontSize: 80, 
          color: 'error.main',
          opacity: 0.6,
        }} 
      />
      
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 400 }}>
        <Typography variant="h6" fontWeight={600} color="error">
          Something Went Wrong
        </Typography>
        
        <Alert severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
        
        <Typography variant="body2" color="text.secondary">
          We couldn't set up the chat. This might be a temporary issue.
        </Typography>
        
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
            disabled={isCreating || createConversationMutation.isLoading}
          >
            Try Again
          </Button>
          
          <Button
            variant="text"
            onClick={handleClose}
          >
            Close
          </Button>
        </Stack>
      </Stack>
    </ErrorContainer>
  );

  const renderLoading = () => {
    const loadingMessage = isCreating || createConversationMutation.isLoading
      ? 'Creating conversation...'
      : isCheckingConversation
      ? 'Checking conversation...'
      : 'Setting up chat...';

    return (
      <LoadingContainer>
        <CircularProgress size={60} thickness={4} />
        
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {loadingMessage}
        </Typography>
        
        <Typography variant="body2" color="text.disabled">
          Please wait while we prepare your conversation with {displayName}
        </Typography>
      </LoadingContainer>
    );
  };

  const renderChat = () => (
    <Fade in timeout={400}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <ChatScreen
          ref={chatRef}
          conversationId={conversationId}
          sx={{
            flex: 1,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
          showWarning={false}
          margin={isMobile ? 1 : 2}
          chatSpacing={isMobile ? 0.5 : 1}
        />
      </Box>
    </Fade>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  if (!open) return null;

  return (
    <FullscreenDrawer
      anchor="right"
      open={open}
      onClose={handleClose}
      variant="temporary"
      disablePortal
      ModalProps={{
        keepMounted: false,
        disableScrollLock: true,
      }}
      transitionDuration={300}
    >
      <Fade in={open} timeout={300}>
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* ───────────────────────────────────────────────────────────────── */}
          {/* HEADER */}
          {/* ───────────────────────────────────────────────────────────────── */}
          
          <HeaderContainer>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                <IconButton
                  onClick={handleClose}
                  sx={{
                    bgcolor: alpha(theme.palette.action.hover, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.action.hover, 0.2),
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  {isMobile ? <ArrowBackIcon /> : <CloseIcon />}
                </IconButton>
                
                <Avatar
                  src={avatarUrl}
                  alt={displayName}
                  sx={{
                    width: 48,
                    height: 48,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    boxShadow: 2,
                  }}
                >
                  {!avatarUrl && displayName?.charAt(0)?.toUpperCase()}
                </Avatar>
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    noWrap
                    sx={{ color: 'text.primary' }}
                  >
                    {displayName}
                  </Typography>
                  
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ChatBubbleOutlineIcon 
                      sx={{ 
                        fontSize: 14, 
                        color: 'text.secondary' 
                      }} 
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Casting Call Chat
                    </Typography>
                    
                    {conversationId && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          ml: 0.5,
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              </Box>
            </Box>
          </HeaderContainer>

          {/* ───────────────────────────────────────────────────────────────── */}
          {/* INFO CHIPS */}
          {/* ───────────────────────────────────────────────────────────────── */}
          
          {castingCall && (
            <ChipsContainer>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Chip
                  color="primary"
                  variant="filled"
                  label={castingCall.brief}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    boxShadow: 1,
                    maxWidth: '100%',
                  }}
                />
                
                {applicant?.assigned_payout && (
                  <Chip
                    color="success"
                    variant="outlined"
                    label={`Payout: $${applicant.assigned_payout.toFixed(2)}`}
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.75rem',
                    }}
                  />
                )}
                
                <Chip
                  size="small"
                  label={castingCall.call_type === 'creator' ? 'Creator Call' : 'General Work'}
                  variant="outlined"
                />
              </Box>
            </ChipsContainer>
          )}

          {/* ───────────────────────────────────────────────────────────────── */}
          {/* CHAT BODY */}
          {/* ───────────────────────────────────────────────────────────────── */}
          
          <ChatContainer>
            {error ? (
              renderError()
            ) : conversationId ? (
              renderChat()
            ) : (
              renderLoading()
            )}
          </ChatContainer>
        </Box>
      </Fade>
    </FullscreenDrawer>
  );
}